import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import AnimatedBackground from '@/components/AnimatedBackground';
import { Briefcase, MapPin, Calendar, ArrowRight, Building2, Code2, GraduationCap, ChevronRight, BookOpen, Brain, ChevronDown, Filter, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Experience: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const experiences = [
    {
      title: 'Founding Engineer - Cloud, Data & NHI Security',
      company: 'CyberDesk GmbH',
      period: 'Oct 2023 – Present',
      location: 'Munich, Germany (Remote)',
      description: 'Leading backend, cloud automation, and security-focused initiatives across data classification, NHI discovery, and identity workflows in modern SaaS environments.',
      techStack: [
        // Cloud Platforms
        'AWS',
        'Microsoft Azure',
        'Google Cloud Platform (GCP)',
        
        // Programming Languages
        'Python',
        'TypeScript',
        'Bun.js',
        
        // Cloud Architecture & Infrastructure
        'Cloud-Native Architecture',
        'Serverless Framework',
        'AWS Step Functions',
        'Pulumi',
        'Distributed Systems',
        
        // DevOps & CI/CD
        'DevOps',
        'GitHub Actions',
        'CI/CD',
        
        // Data Processing & Analytics
        'ETL',
        'Elastic Stack (ELK)',
        'Presto',
        'Redis',
        
        // AI & Machine Learning
        'Natural Language Processing (NLP)',
        'Large Language Models (LLM)',
        
        // Security & Data Processing
        'Microsoft Presidio',
        'Apache Tika',
        'MS365 Graph API',
        'Google Workspace API',
        
        // Frameworks & Tools
        'FastAPI',
        'Docker',
        'Kubernetes',
        'Terraform'
      ],
      type: 'work',
      highlights: [
        'Architected core backend infrastructure processing millions of documents daily for text classification and metadata extraction services',
        'Designed high-performance data scanning engine combining LLMs, NER models, and regex patterns to detect sensitive data (PII, PHI, financial) with 95%+ accuracy across 10M+ documents daily',
        'Built real-time alerting pipelines monitoring user permissions and detecting suspicious activity across AWS, Azure, GCP, and SaaS platforms, processing 100k+ audit logs daily',
        'Engineered data discovery system for data stores and assets across cloud providers and SaaS platforms (Microsoft 365, Google Workspace) at scale',
        'Implemented event-driven data streaming systems using DynamoDB Streams and serverless functions for real-time data synchronization across multiple services',
        'Streamlined deployment processes using Serverless Framework and GitHub Actions, reducing deployment time from hours to minutes with automated CI/CD pipelines'
      ]
    },
    {
      title: 'Founding Engineer - Cloud & Data Security',
      company: 'Normalyze Inc.(Acquired by Proofpoint)',
      period: 'Feb 2022 – Oct 2023',
      location: 'Los Altos, CA (Remote)',
      description: 'Built scalable connectors and ingestion pipelines for RDS, DynamoDB, Azure SQL, GCP, and file systems using AWS Lambda and Azure Functions. Worked on graph data modeling, security fixes, and code hardening.',
      techStack: [
        // Cloud Platforms
        'AWS',
        'Microsoft Azure',
        'Google Cloud Platform (GCP)',
        
        // Programming Languages
        'Python',
        'JavaScript',
        'Node.js',
        'Go',
        
        // Databases & Data Storage
        'PostgreSQL',
        'MySQL',
        'Redis',
        'Memcached',
        'neo4j (Graph Database)',
        'Databases',
        
        // Infrastructure & DevOps
        'Docker',
        'Docker Compose',
        'Terraform',
        'GitHub Actions',
        'Distributed Systems',
        
        // Frameworks & Tools
        'Fastify',
        'Netflix Conductor',
        'Elastic Stack (ELK)'
      ],
      type: 'work',
      highlights: [
        'Built Python-based database connector package for AWS, Azure, and GCP specializing in large-scale content extraction from cloud databases (RDS, DynamoDB, CosmosDB), handling millions of records daily',
        'Developed robust file processing system for NFS and SMB file systems (AWS EFS, Azure Files, Google Cloud Filestore), processing millions of files and multi-terabytes of data daily',
        'Built comprehensive vulnerability scanning system using Trivy database with automated daily sync, scanning file systems, container images, and IaC configurations across entire infrastructure',
        'Architected distributed data processing workflows using Netflix Conductor orchestrator and Neo4j graph database to handle unstructured data at massive scale',
        'Implemented concurrent VM orchestration system managing 100+ processing instances for real-time data ingestion, processing multiple terabytes of unstructured data daily',
        'Revamped critical codebase components implementing modern design patterns, standardized error handling, and comprehensive testing frameworks achieving 70%+ code coverage'
      ]
    },
    {
      title: 'Software Engineer - Backend (Video Commerce)',
      company: 'Woovly',
      period: 'Mar 2021 – Jul 2022',
      location: 'Bangalore, India',
      description: 'Built APIs, CI/CD pipelines, and serverless microservices for a video commerce platform. Migrated monoliths to microservices and improved pagination, caching, and file upload performance.',
      techStack: [
        // Cloud & Infrastructure
        'AWS',
        'Jenkins',
        
        // Programming Languages
        'JavaScript',
        'Node.js',
        'Python',
        
        // Frameworks & Libraries
        'Express.js',
        
        // Databases
        'MongoDB',
        'MySQL'
      ],
      type: 'work',
      highlights: [
        'Built scalable microservice exposing 100+ RESTful APIs for mobile applications, handling 10+ lakh users with 99.9% availability and sub-200ms response times',
        'Led complex migration from monolithic to microservice architecture, refactoring 100+ APIs into 15+ independent services, achieving 300% faster deployments and 80% reduction in system downtime',
        'Architected real-time order tracking data pipeline using AWS Fan-out & Fan-in pattern with SNS, SQS, and Lambda, processing 10,000+ orders daily with 99.9% reliability and sub-second latency',
        'Designed Redis caching layer across critical services (shopping cart, user sessions, product catalog), reducing API response times by 80% and improving throughput by 300% for 50,000+ concurrent users',
        'Migrated media file upload service to AWS serverless architecture using Lambda, S3, and CloudFront, reducing upload times from minutes to seconds with 99.9% success rate for 10,000+ daily uploads'
      ]
    },
    {
      title: 'Software Engineer Intern - Backend (Health Tech)',
      company: 'LeewayHertz',
      period: 'Dec 2020 – Apr 2021',
      location: 'Gurugram, India',
      description: 'Optimized backend services with Redis caching, JWT authentication, and performance benchmarks. Contributed React components and REST APIs.',
      techStack: [
        // Programming & Frameworks
        'Node.js',
        'Next.js',
        
        // Databases
        'PostgreSQL',
        
        // Infrastructure & DevOps
        'Docker',
        'Docker Swarm',
        'Jenkins',
        
        // Message Queue
        'RabbitMQ'
      ],
      type: 'work',
      highlights: [
        'Optimized data retrieval latency by implementing LRU eviction policy with Redis cache, enhancing overall application performance',
        'Conducted benchmarking of DBMS query execution and performed stress testing using loader.io, resulting in significant latency reduction',
        'Designed and implemented reusable ReactJS components including multi-field form elements and background file uploads, improving development efficiency',
        'Strengthened application security by building JWT Authentication system with refresh mechanism, ensuring secure user authentication and authorization',
        'Made substantial contributions to backend development, creating numerous REST APIs for various business products',
        'Developed modular web-based SaaS applications within agile and dynamic environment, adapting to changes effectively'
      ]
    },
    {
      title: 'Research Intern',
      company: 'The Hong Kong University of Science and Technology',
      period: 'May 2020 – Oct 2020',
      location: 'Hong Kong SAR (Remote)',
      description: 'Researched dependency analysis in build systems. Contributed to Unified Dependency Graph design and evaluated tools like VeriBuild across OSS projects.',
      techStack: [
        // Programming & Tools
        'Python',
        'Bash',
        'Git',
        'Make',
        'CI Tools',
        
        // Research & Analysis
        'Machine Learning',
        'Statistical Data Analysis',
        'Mathematical Modeling',
        'Mathematical Optimization',
        'Operations Research'
      ],
      type: 'research',
      highlights: [
        'Analyzed build system challenges including missing dependencies and redundant dependencies, focusing on improving build accuracy and performance in software development',
        'Researched and contributed to the design of Unified Dependency Graph (UDG), integrating static and dynamic dependency information for better consistency in build systems',
        'Implemented techniques for detecting dependency-related errors in build scripts, including identifying missing dependencies (MDs) and redundant dependencies (RDs)',
        'Developed and tested methods to enhance incremental builds and parallel builds, minimizing overhead and increasing efficiency',
        'Conducted evaluations of dependency verification tools like VeriBuild, demonstrating superior precision and scalability over state-of-the-art techniques',
        'Collaborated on experiments with 42 open-source projects, analyzing over 2,000 dependency issues and presenting findings to improve build reliability'
      ]
    },
    {
      title: 'Software Developer Intern - Web',
      company: 'GirlScript Foundation',
      period: 'Dec 2019 – Mar 2020',
      location: 'Nagpur, India (Remote)',
      description: 'Built dashboards using GitHub APIs, improved UI/UX, and modularized frontend and backend for better collaboration.',
      techStack: ['JavaScript', 'GitHub API', 'React', 'CSS', 'REST APIs'],
      type: 'work',
      highlights: [
        'Developed comprehensive dashboard enabling participants to monitor progress and scores based on GitHub activity (commits, pull requests, issues) using GitHub REST API',
        'Conducted extensive refactoring and modularization of existing codebase, resulting in streamlined workflow and enhanced collaboration for the entire team',
        'Contributed to enhanced user experience by designing and implementing custom UI components (buttons, containers, layouts) and revamping homepage, project page, and team page',
        'Actively contributed to code reviews and provided constructive feedback to fellow team members, fostering collaborative and quality-focused development environment',
        'Employed data visualization techniques to present participant progress and scores in visually appealing and easily understandable manner'
      ]
    },
    {
      title: 'Software Engineer Intern - Backend',
      company: 'JPMorgan Chase & Co.',
      period: 'Apr 2019 – Jul 2019',
      location: 'Bangalore, India',
      description: 'Built Java-based XML parser, integrated Kafka with Spring Boot, and developed dashboards for notification visualization.',
      techStack: ['Java', 'Spring Boot', 'Kafka', 'Docker', 'Angular'],
      type: 'work',
      highlights: [
        'Developed Java parser to efficiently transform XML messages received from upstream sources, enhancing data processing capabilities',
        'Seamlessly integrated Apache Kafka with middle tier built on Spring Boot and Spring Webflux, enabling efficient and scalable message handling',
        'Created user-friendly dashboard using Angular, providing visually appealing and intuitive interface for viewing notifications',
        'Utilized Docker to containerize the application, ensuring easy deployment and consistent environments across different platforms',
        'Implemented comprehensive testing strategies including unit tests and integration tests, ensuring reliability and stability of the application',
        'Successfully pushed the application to QA environment, facilitating thorough quality assurance testing and validation before production deployment'
      ]
    },
    {
      title: 'Machine Learning Intern',
      company: 'OpenGenus Foundation',
      period: 'Mar 2019 – Jun 2019',
      location: 'Remote',
      description: 'Worked on regression and KNN models, wrote technical blogs, and contributed code and insights to the ML open-source community.',
      techStack: ['Python', 'Scikit-learn', 'Pandas', 'NumPy', 'Matplotlib'],
      type: 'ml',
      highlights: [
        'Implemented normalization and standardization preprocessing techniques to improve performance of regression and KNN models by reducing loss',
        'Conducted Grid search to find optimal hyperparameters for machine learning models, resulting in improved accuracy and performance',
        'Contributed to OpenGenus open-source community on GitHub by sharing code, submitting bug reports, and helping other users solve issues',
        'Wrote informative articles on various machine learning topics for OpenGenus blog, providing valuable insights and analysis to wider community',
        'Worked on multiple machine learning and deep learning projects, gaining hands-on experience and improving proficiency in data science technologies',
        'Collaborated with other data scientists and researchers to develop and test new algorithms and techniques, contributing to advancement of machine learning field'
      ]
    },
    {
      title: 'Software Engineer Intern - Backend',
      company: 'Oracle',
      period: 'Apr 2018 – Jul 2018',
      location: 'Bangalore, India',
      description: 'Worked on internal tooling, CI/CD automation, and caching with Redis. Gained exposure to scalable backend practices.',
      techStack: ['Java', 'Redis', 'Jenkins', 'GitLab CI', 'Spring'],
      type: 'work',
      highlights: [
        'Internal tooling',
        'CI/CD automation',
        'Redis caching'
      ]
    }
  ];

  const getIcon = (type: string) => {
    switch (type) {
      case 'work':
        return <Building2 className="w-4 h-4" />;
      case 'research':
        return <Code2 className="w-4 h-4" />;
      case 'ml':
        return <Brain className="w-4 h-4" />;
      default:
        return <GraduationCap className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'work':
        return 'Work Experience';
      case 'research':
        return 'Research';
      case 'ml':
        return 'Machine Learning';
      default:
        return 'Other';
    }
  };

  const filteredExperiences = experiences.filter(exp => 
    selectedFilter === 'all' || exp.type === selectedFilter
  );

  const toggleCard = (index: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCards(newExpanded);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    hover: {
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  const headerVariants = {
    hidden: { 
      opacity: 0,
      y: -30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }
  };

  return (
    <div className="min-h-screen relative">
      <AnimatedBackground />
      <Navigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div 
            variants={headerVariants}
            initial="hidden"
            animate="visible"
            className="relative mb-8"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-2xl transform -rotate-1"></div>
            <div className="relative bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-center gap-3 mb-4">
                <motion.div 
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl"
                >
                  <Briefcase className="w-5 h-5 text-white" />
                </motion.div>
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
                  Experience
                </h1>
              </div>
              <p className="text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-center">
                My professional journey through various roles and companies, focusing on cloud technologies, security, and software engineering.
              </p>
            </div>
          </motion.div>

          {/* Filter Section */}
          <motion.div 
            variants={headerVariants}
            initial="hidden"
            animate="visible"
            className="mb-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['all', 'work', 'research', 'ml'].map((filter) => (
                <motion.button
                  key={filter}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedFilter === filter
                      ? 'bg-green-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {filter === 'all' ? 'All' : getTypeLabel(filter)}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Timeline Layout */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative"
          >
            {/* Timeline Line */}
            <div className="absolute left-4 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 via-blue-500 to-cyan-500 hidden lg:block"></div>
            
            <div className="space-y-6">
              <AnimatePresence>
                {filteredExperiences.map((exp, index) => (
                  <motion.div
                    key={index}
                    variants={cardVariants}
                    whileHover="hover"
                    className="group relative"
                  >
                    {/* Timeline Dot */}
                    <div className="absolute left-2 sm:left-6 top-6 w-4 h-4 bg-gradient-to-r from-green-500 to-blue-500 rounded-full border-4 border-white dark:border-gray-800 shadow-lg hidden lg:block"></div>
                    
                    <div className="lg:ml-12">
                      <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:border-green-500/50 dark:hover:border-green-500/50 transition-all duration-300 hover:shadow-lg">
                        <div className="p-4 sm:p-6">
                          {/* Header Row */}
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                            <div className="flex items-start gap-3">
                              <motion.div 
                                whileHover={{ rotate: 360 }}
                                transition={{ duration: 0.5 }}
                                className="p-2 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex-shrink-0"
                              >
                                {getIcon(exp.type)}
                              </motion.div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors mb-1">
                                  {exp.title}
                                </h3>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{exp.company}</p>
                                <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>{exp.period}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    <span>{exp.location}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => toggleCard(index)}
                              className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                            >
                              <ChevronDown 
                                className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform duration-200 ${
                                  expandedCards.has(index) ? 'rotate-180' : ''
                                }`} 
                              />
                            </motion.button>
                          </div>

                          {/* Description */}
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                            {exp.description}
                          </p>

                          {/* Expandable Content */}
                          <AnimatePresence>
                            {expandedCards.has(index) && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-4"
                              >
                                {/* Tech Stack */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                                    Tech Stack
                                  </h4>
                                  <div className="flex flex-wrap gap-1.5">
                                    {exp.techStack.map((tech, idx) => (
                                      <motion.span
                                        key={idx}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: idx * 0.05 }}
                                        whileHover={{ 
                                          scale: 1.1,
                                          rotate: [0, -5, 5, -5, 0],
                                          transition: { duration: 0.5 }
                                        }}
                                        className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full"
                                      >
                                        {tech}
                                      </motion.span>
                                    ))}
                                  </div>
                                </div>

                                {/* Highlights */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                                    Key Achievements
                                  </h4>
                                  <div className="space-y-2">
                                    {exp.highlights.map((highlight, idx) => (
                                      <motion.div 
                                        key={idx}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: idx * 0.1 }}
                                        className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400"
                                      >
                                        <motion.div
                                          whileHover={{ rotate: 360 }}
                                          transition={{ duration: 0.5 }}
                                          className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"
                                        />
                                        <span className="leading-relaxed">{highlight}</span>
                                      </motion.div>
                                    ))}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Experience; 